import { z } from "zod";

export const FORM_FIELD_TYPES = ["text", "multiline", "choice", "multichoice", "select", "number", "boolean", "date", "secret"] as const;
export type FormFieldType = (typeof FORM_FIELD_TYPES)[number];

export const FORM_LIMITS = {
  maxFields: 24,
  maxOptions: 32,
  maxLabelLength: 200,
  maxHelperLength: 500,
  maxTextLength: 4_000,
  minNumber: -1_000_000_000,
  maxNumber: 1_000_000_000,
} as const;

const idSchema = z.string().min(1).max(64).regex(/^[a-z0-9][a-z0-9_-]*$/i, "field ids must be alphanumeric with - or _");
const labelSchema = z.string().min(1).max(FORM_LIMITS.maxLabelLength);
const helperSchema = z.string().max(FORM_LIMITS.maxHelperLength);

const optionSchema = z.object({
  id: idSchema,
  label: labelSchema,
});

const baseField = {
  id: idSchema,
  label: labelSchema,
  helper: helperSchema.optional(),
  required: z.boolean().optional(),
};

const strict = <T extends z.ZodRawShape>(shape: T) => z.object(shape).strict();
const fieldSchema = z.discriminatedUnion("type", [
  strict({ ...baseField, type: z.literal("text"), maxLength: z.number().int().min(1).max(FORM_LIMITS.maxTextLength).optional() }),
  strict({ ...baseField, type: z.literal("multiline"), maxLength: z.number().int().min(1).max(FORM_LIMITS.maxTextLength).optional() }),
  strict({ ...baseField, type: z.literal("choice"), options: z.array(optionSchema).min(2).max(FORM_LIMITS.maxOptions) }),
  strict({ ...baseField, type: z.literal("select"), options: z.array(optionSchema).min(2).max(FORM_LIMITS.maxOptions) }),
  strict({ ...baseField, type: z.literal("multichoice"), options: z.array(optionSchema).min(2).max(FORM_LIMITS.maxOptions), minSelected: z.number().int().min(0).optional(), maxSelected: z.number().int().min(1).max(FORM_LIMITS.maxOptions).optional() }),
  strict({ ...baseField, type: z.literal("number"), min: z.number().min(FORM_LIMITS.minNumber).max(FORM_LIMITS.maxNumber).optional(), max: z.number().min(FORM_LIMITS.minNumber).max(FORM_LIMITS.maxNumber).optional(), integer: z.boolean().optional() }),
  strict({ ...baseField, type: z.literal("boolean") }),
  strict({ ...baseField, type: z.literal("date"), timezone: z.string().min(1).max(64).optional() }),
  strict({ ...baseField, type: z.literal("secret"), kind: z.enum(["password", "api_key", "otp", "pin"]) }),
]);

export const FormSchema = strict({
  schemaVersion: z.literal(1),
  title: labelSchema,
  description: helperSchema.optional(),
  fields: z.array(fieldSchema).max(FORM_LIMITS.maxFields),
  submitLabel: labelSchema.optional(),
  allowAnswerWithoutAudio: z.boolean().optional(),
}).superRefine((form, ctx) => {
  const seen = new Set<string>();
  for (const [index, field] of form.fields.entries()) {
    if (seen.has(field.id)) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["fields", index, "id"], message: `duplicate field id ${field.id}` });
    seen.add(field.id);
    if ("options" in field) {
      const optionIds = new Set<string>();
      for (const [optionIndex, option] of field.options.entries()) {
        if (optionIds.has(option.id)) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["fields", index, "options", optionIndex, "id"], message: `duplicate option id ${option.id}` });
        optionIds.add(option.id);
      }
    }
    if (field.type === "number" && field.min !== undefined && field.max !== undefined && field.min > field.max) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["fields", index, "max"], message: "max must not be below min" });
    }
    if (field.type === "multichoice" && field.minSelected !== undefined && field.maxSelected !== undefined && field.minSelected > field.maxSelected) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["fields", index, "maxSelected"], message: "maxSelected must not be below minSelected" });
    }
  }
});

export type FormDefinition = z.infer<typeof FormSchema>;
export type FormField = z.infer<typeof fieldSchema>;

/**
 * Words that make a field a credential even when the schema did not declare one.
 * A form may not downgrade these to model-visible text: the answer has to travel
 * through the protected credential path instead.
 */
export const FORM_CREDENTIAL_PATTERN = /(?:password|passwd|passphrase|secret|token|api[\s_-]?key|access[\s_-]?key|private[\s_-]?key|signing[\s_-]?key|credential|\bpin\b|\botp\b|one[\s_-]?time|\b2fa\b|\bmfa\b|(?:^|[\s_-])keys?$)/i;

export type FormFieldProtection = "declared-secret" | "credential-like" | "ordinary";

export function looksLikeCredentialField(field: { readonly id: string; readonly label: string }): boolean {
  return FORM_CREDENTIAL_PATTERN.test(field.id) || FORM_CREDENTIAL_PATTERN.test(field.label);
}

/**
 * How a field must be answered: a declared secret, a field whose wording makes it a
 * credential, or an ordinary field the user may still choose to protect.
 */
export function formFieldProtection(field: FormField): FormFieldProtection {
  if (field.type === "secret") return "declared-secret";
  return looksLikeCredentialField(field) ? "credential-like" : "ordinary";
}

/** One answer the user chose to protect, already sealed by the server. */
export interface ProtectedFormAnswer {
  readonly fieldId: string;
  readonly handle: string;
}

export interface FormValidationOptions {
  readonly protectedAnswers?: readonly ProtectedFormAnswer[];
  /** Fields the user explicitly confirmed are not credentials, so text is allowed. */
  readonly ordinaryFieldIds?: readonly string[];
}

export const FORM_SECRET_HANDLE_PATTERN = /^\{\{secret:[A-Za-z0-9_-]{1,64}\}\}$/;

export function isFormSecretHandle(value: unknown): value is string {
  return typeof value === "string" && FORM_SECRET_HANDLE_PATTERN.test(value);
}

export interface FormAnswerError {
  readonly fieldId: string;
  readonly message: string;
}

export interface FormAnswerValidation {
  readonly ok: boolean;
  readonly errors: readonly FormAnswerError[];
  readonly accepted: Readonly<Record<string, string | number | boolean | readonly string[]>>;
  readonly secretFieldIds: readonly string[];
  readonly requiredSecretFieldIds: readonly string[];
}

export type FormAnswers = Readonly<Record<string, unknown>>;

function isPlainDate(value: string): boolean {
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) && /^\d{4}-\d{2}-\d{2}(?:[T ].*)?$/.test(value);
}

export function validateFormAnswers(form: FormDefinition, answers: FormAnswers, options: FormValidationOptions = {}): FormAnswerValidation {
  const errors: FormAnswerError[] = [];
  const accepted: Record<string, string | number | boolean | readonly string[]> = {};
  const secretFieldIds: string[] = [];
  const requiredSecretFieldIds: string[] = [];
  const protectedByField = new Map((options.protectedAnswers ?? []).map((answer) => [answer.fieldId, answer.handle]));
  const ordinary = new Set(options.ordinaryFieldIds ?? []);
  const known = new Set(form.fields.map((field) => field.id));
  for (const key of Object.keys(answers)) {
    if (!known.has(key)) errors.push({ fieldId: key, message: "unknown field" });
  }
  for (const field of form.fields) {
    const value = answers[field.id];
    const required = field.required === true;
    const handle = protectedByField.get(field.id);
    if (field.type === "secret") {
      secretFieldIds.push(field.id);
      if (required) requiredSecretFieldIds.push(field.id);
      if (handle !== undefined) {
        if (value !== undefined && value !== null && value !== "" && value !== handle) {
          errors.push({ fieldId: field.id, message: "the protected value and a plain value cannot both be submitted" });
          continue;
        }
        accepted[field.id] = handle;
        continue;
      }
      if (value !== undefined && value !== null && value !== "") {
        errors.push({ fieldId: field.id, message: "credentials must be submitted through the protected credential field" });
      }
      continue;
    }
    if (handle !== undefined) {
      if (value !== undefined && value !== null && value !== "" && value !== handle) {
        errors.push({ fieldId: field.id, message: "the protected value and a plain value cannot both be submitted" });
        continue;
      }
      accepted[field.id] = handle;
      continue;
    }
    if (looksLikeCredentialField(field) && !ordinary.has(field.id)) {
      const plaintext = typeof value === "string" && value.trim().length > 0;
      if (plaintext) {
        errors.push({ fieldId: field.id, message: "this field looks like a credential; protect the answer instead of sending it as plain text" });
        continue;
      }
      if (required) errors.push({ fieldId: field.id, message: "this field is required" });
      continue;
    }
    if (value === undefined || value === null || value === "") {
      if (required) errors.push({ fieldId: field.id, message: "this field is required" });
      continue;
    }
    switch (field.type) {
      case "text":
      case "multiline": {
        if (typeof value !== "string") { errors.push({ fieldId: field.id, message: "expected text" }); break; }
        const max = field.maxLength ?? FORM_LIMITS.maxTextLength;
        if (value.length > max) errors.push({ fieldId: field.id, message: `must be at most ${max} characters` });
        else accepted[field.id] = value;
        break;
      }
      case "choice":
      case "select": {
        if (typeof value !== "string" || !field.options.some((option) => option.id === value)) errors.push({ fieldId: field.id, message: "not a supported option" });
        else accepted[field.id] = value;
        break;
      }
      case "multichoice": {
        if (!Array.isArray(value) || value.some((entry) => typeof entry !== "string")) { errors.push({ fieldId: field.id, message: "expected a list of options" }); break; }
        const selected = [...new Set(value as string[])];
        if (selected.length !== (value as string[]).length) { errors.push({ fieldId: field.id, message: "duplicate selections are not allowed" }); break; }
        if (selected.some((entry) => !field.options.some((option) => option.id === entry))) { errors.push({ fieldId: field.id, message: "not a supported option" }); break; }
        if (field.minSelected !== undefined && selected.length < field.minSelected) { errors.push({ fieldId: field.id, message: `select at least ${field.minSelected}` }); break; }
        if (field.maxSelected !== undefined && selected.length > field.maxSelected) { errors.push({ fieldId: field.id, message: `select at most ${field.maxSelected}` }); break; }
        accepted[field.id] = selected;
        break;
      }
      case "number": {
        if (typeof value !== "number" || !Number.isFinite(value)) { errors.push({ fieldId: field.id, message: "expected a number" }); break; }
        if (field.integer === true && !Number.isInteger(value)) { errors.push({ fieldId: field.id, message: "expected a whole number" }); break; }
        if (field.min !== undefined && value < field.min) { errors.push({ fieldId: field.id, message: `must be at least ${field.min}` }); break; }
        if (field.max !== undefined && value > field.max) { errors.push({ fieldId: field.id, message: `must be at most ${field.max}` }); break; }
        accepted[field.id] = value;
        break;
      }
      case "boolean": {
        if (typeof value !== "boolean") errors.push({ fieldId: field.id, message: "expected yes or no" });
        else accepted[field.id] = value;
        break;
      }
      case "date": {
        if (typeof value !== "string" || !isPlainDate(value)) errors.push({ fieldId: field.id, message: "expected an unambiguous date" });
        else accepted[field.id] = value;
        break;
      }
    }
  }
  return { ok: errors.length === 0, errors, accepted, secretFieldIds, requiredSecretFieldIds };
}

/**
 * Stable revision of one clarification's form, computed the same way on the server
 * and in every client. A submission that echoes an older revision is refusing to
 * answer questions that have since changed.
 */
export function formRevision(form: FormDefinition, clarificationId: string): string {
  const payload = JSON.stringify({ v: 1, clarificationId, form });
  let hash = 0x811c9dc5;
  for (let index = 0; index < payload.length; index += 1) {
    hash ^= payload.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return `fnv1a:${hash.toString(16).padStart(8, "0")}`;
}
